import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ws } from "./api";
import { PageHeader, Panel, Empty, Loading } from "./ui";
import { TaskDialog } from "./TasksPage";

const dayKey = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
const time = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

export const AgendaList = ({ mine = false, reloadKey = 0 }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    ws(`/crm/workspace/tasks?status=open${mine ? "&mine=true" : ""}`)
      .then((r) => setItems(r.tasks.filter((t) => t.due_at && new Date(t.due_at) >= new Date(Date.now() - 864e5))))
      .catch((e) => toast.error(e.message));
  }, [mine, reloadKey]);
  if (!items) return <Loading />;
  if (items.length === 0) return <Empty icon={CalendarDays} title="Nothing scheduled" hint="Upcoming meetings and dated tasks appear here." />;
  const groups = {};
  items.forEach((t) => (groups[new Date(t.due_at).toDateString()] ||= []).push(t));
  return (
    <div className="space-y-5" data-testid="agenda-list">
      {Object.entries(groups).map(([day, list]) => (
        <div key={day}>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{new Date(day).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}</p>
          <ul className="space-y-1.5">{list.map((t) => (
            <li key={t.task_id} className="flex items-center gap-3 text-sm" data-testid={`agenda-item-${t.task_id}`}>
              <span className="w-12 text-xs text-slate-500">{time(t.due_at)}</span>
              <span className={`h-2 w-2 rounded-full ${t.kind === "meeting" ? "bg-[#E8A33D]" : "bg-[#0B1530]"}`} />
              <span className="text-slate-800">{t.title}</span><span className="text-[10px] uppercase text-slate-400">{t.kind}</span>
            </li>))}</ul>
        </div>
      ))}
    </div>
  );
};

const Month = ({ cursor, items }) => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first); start.setDate(1 - ((first.getDay() + 6) % 7));
  const byDay = {};
  items.forEach((t) => t.due_at && (byDay[dayKey(t.due_at)] ||= []).push(t));
  const today = dayKey(new Date().toISOString());
  return (
    <div className="grid grid-cols-7 border-l border-t border-slate-200 rounded-lg overflow-hidden text-xs" data-testid="calendar-month">
      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="bg-slate-50 border-r border-b border-slate-200 px-2 py-1.5 font-medium text-slate-500">{d}</div>)}
      {Array.from({ length: 42 }, (_, i) => {
        const d = new Date(start); d.setDate(start.getDate() + i);
        const k = dayKey(d.toISOString());
        const list = byDay[k] || [];
        return (
          <div key={i} className={`min-h-[84px] border-r border-b border-slate-200 p-1.5 ${d.getMonth() !== cursor.getMonth() ? "bg-slate-50/60 text-slate-300" : "bg-white"}`}>
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${k === today ? "bg-[#0B1530] text-white" : ""}`}>{d.getDate()}</span>
            {list.slice(0, 3).map((t) => <p key={t.task_id} className={`truncate mt-0.5 rounded px-1 ${t.kind === "meeting" ? "bg-[#E8A33D]/15 text-[#7a4f10]" : "bg-[#0B1530]/[0.06] text-[#0B1530]"}`}>{t.title}</p>)}
            {list.length > 3 && <p className="text-slate-400 mt-0.5">+{list.length - 3} more</p>}
          </div>
        );
      })}
    </div>
  );
};

export default function CalendarPage() {
  const [params, setParams] = useSearchParams();
  const [cursor, setCursor] = useState(() => new Date());
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(params.get("create") === "meeting");
  const [k, setK] = useState(0);
  useEffect(() => { ws("/crm/workspace/tasks").then((r) => setItems(r.tasks)).catch((e) => toast.error(e.message)); }, [k]);
  useEffect(() => { if (params.get("create")) { setOpen(true); setParams({}, { replace: true }); } }, [params, setParams]);
  const shift = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  return (
    <div data-testid="calendar-page">
      <PageHeader title="Calendar" subtitle="Meetings and dated tasks"
        actions={<Button onClick={() => setOpen(true)} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="new-meeting-btn"><Plus className="h-4 w-4 mr-1" />Schedule meeting</Button>} />
      <div className="grid xl:grid-cols-3 gap-4">
        <Panel className="xl:col-span-2" title={cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })} action={
          <div className="flex gap-1"><button onClick={() => shift(-1)} className="p-1 hover:bg-slate-100 rounded" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button><button onClick={() => shift(1)} className="p-1 hover:bg-slate-100 rounded" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button></div>}>
          <div className="overflow-x-auto"><div className="min-w-[640px]"><Month cursor={cursor} items={items} /></div></div>
        </Panel>
        <Panel title="Agenda"><AgendaList reloadKey={k} /></Panel>
      </div>
      <TaskDialog open={open} onOpenChange={setOpen} kind="meeting" onSaved={() => setK(k + 1)} />
    </div>
  );
}
